// AstriaIndiaV2Card.tsx
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Share2, MessageCircleHeart, Copy, Check, Download } from "lucide-react";
import { SHELL_COLORS_INDIA_V2, INDIA_V2_HEADING_FONT } from "./AstriaIndiaV2Shell";

// ─── Astria India v2 — premium result-card primitives ──────────────────────
// Large title, icon, backend text rendered exactly as received, soft
// gradient, rounded corners, subtle decorative geometric pattern, share row.

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  visible: (delay: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay,
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1] as [number, number, number, number],
    },
  }),
};

// Subtle Indian-inspired geometric pattern (mandala dot-ring), very low
// opacity, purely decorative, never obscures text.
const DecorativePattern: React.FC<{ accent: string }> = ({ accent }) => (
  <svg
    width="120"
    height="120"
    viewBox="0 0 120 120"
    style={{
      position: "absolute",
      top: -20,
      right: -20,
      opacity: 0.07,
      pointerEvents: "none",
    }}
  >
    <circle cx="60" cy="60" r="50" fill="none" stroke={accent} strokeWidth="1.5" />
    <circle cx="60" cy="60" r="36" fill="none" stroke={accent} strokeWidth="1.5" />
    {Array.from({ length: 12 }).map((_, i) => {
      const angle = (i * 30 * Math.PI) / 180;
      const x = 60 + 50 * Math.cos(angle);
      const y = 60 + 50 * Math.sin(angle);
      return <circle key={i} cx={x} cy={y} r="3" fill={accent} />;
    })}
  </svg>
);

interface AstriaIndiaV2CardProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  accent?: [string, string];
  children: React.ReactNode;
  delay?: number;
  shareText?: string;
  shareLabels?: ShareRowProps["labels"];
}

export const AstriaIndiaV2ResultCard: React.FC<AstriaIndiaV2CardProps> = ({
  icon,
  title,
  subtitle,
  accent = [SHELL_COLORS_INDIA_V2.accent, SHELL_COLORS_INDIA_V2.gold],
  children,
  delay = 0,
  shareText,
  shareLabels,
}) => {
  return (
    <motion.div
      custom={delay}
      initial="hidden"
      animate="visible"
      variants={fadeUp}
      whileHover={{ y: -2 }}
      style={{
        position: "relative",
        overflow: "hidden",
        background: `linear-gradient(160deg, ${SHELL_COLORS_INDIA_V2.white} 0%, ${SHELL_COLORS_INDIA_V2.section} 100%)`,
        border: `1px solid ${SHELL_COLORS_INDIA_V2.cardBorder}`,
        borderRadius: 22,
        padding: 28,
        boxShadow: "0 10px 34px rgba(46,31,69,0.08), 0 2px 8px rgba(46,31,69,0.04)",
        transition: "box-shadow 0.3s ease, transform 0.3s ease",
      }}
    >
      <DecorativePattern accent={accent[0]} />

      <div className="flex items-center gap-3" style={{ marginBottom: 18 }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 14,
            background: `linear-gradient(135deg, ${accent[0]} 0%, ${accent[1]} 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            boxShadow: `0 6px 18px ${SHELL_COLORS_INDIA_V2.accentSoft}`,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: SHELL_COLORS_INDIA_V2.titleColor,
              fontFamily: INDIA_V2_HEADING_FONT,
              lineHeight: 1.25,
            }}
          >
            {title}
          </div>
          {subtitle && (
            <div
              style={{
                fontSize: 12.5,
                color: SHELL_COLORS_INDIA_V2.mutedColor,
                marginTop: 2,
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
      </div>

      <div style={{ position: "relative", zIndex: 1 }}>{children}</div>

      <AstriaIndiaV2ShareRow text={shareText} accent={accent[0]} labels={shareLabels} />
    </motion.div>
  );
};

interface FieldBlockProps {
  label: string;
  value: string;
  accent?: string;
}

export const AstriaIndiaV2FieldBlock: React.FC<FieldBlockProps> = ({
  label,
  value,
  accent = SHELL_COLORS_INDIA_V2.accent,
}) => {
  if (!value || !value.trim()) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.09em",
          textTransform: "uppercase",
          color: accent,
          marginBottom: 5,
        }}
      >
        {label}
      </div>
      <p
        style={{
          fontSize: 15,
          lineHeight: 1.75,
          color: SHELL_COLORS_INDIA_V2.bodyColor,
          whiteSpace: "pre-wrap",
        }}
      >
        {value}
      </p>
    </div>
  );
};

export const AstriaIndiaV2Divider: React.FC = () => (
  <div
    style={{
      height: 1,
      background: SHELL_COLORS_INDIA_V2.cardBorder,
      margin: "16px 0",
    }}
  />
);

interface ShareRowProps {
  text?: string;
  accent?: string;
  labels?: {
    whatsApp?: string;
    instagram?: string;
    copy?: string;
    copied?: string;
    saveImage?: string;
  };
}

export const AstriaIndiaV2ShareRow: React.FC<ShareRowProps> = ({
  text,
  accent = SHELL_COLORS_INDIA_V2.accent,
  labels,
}) => {
  const [copied, setCopied] = useState(false);
  const t = {
    whatsApp: labels?.whatsApp || "WhatsApp",
    instagram: labels?.instagram || "Instagram",
    copy: labels?.copy || "Copy",
    copied: labels?.copied || "Copied",
    saveImage: labels?.saveImage || "Save Image",
  };

  if (!text) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // clipboard unavailable — silently ignore
    }
  };

  const handleWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleInstagram = () => {
    // Instagram has no direct web share-text intent; copy to clipboard so
    // the user can paste into their Story/DM, and open Instagram.
    handleCopy();
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
  };

  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        marginTop: 20,
        paddingTop: 18,
        borderTop: `1px solid ${SHELL_COLORS_INDIA_V2.cardBorder}`,
        flexWrap: "wrap",
      }}
    >
      <button
        onClick={handleWhatsApp}
        className="flex items-center gap-1.5 rounded-full text-xs font-semibold transition-all hover:scale-[1.03]"
        style={{
          paddingInline: 14,
          paddingBlock: 9,
          background: "#25D366",
          color: "#fff",
        }}
      >
        <Share2 className="w-3.5 h-3.5" />
        {t.whatsApp}
      </button>
      <button
        onClick={handleInstagram}
        className="flex items-center gap-1.5 rounded-full text-xs font-semibold transition-all hover:scale-[1.03]"
        style={{
          paddingInline: 14,
          paddingBlock: 9,
          background: "linear-gradient(135deg, #F58529, #DD2A7B, #8134AF)",
          color: "#fff",
        }}
      >
        <Share2 className="w-3.5 h-3.5" />
        {t.instagram}
      </button>
      <button
        onClick={handleCopy}
        className="flex items-center gap-1.5 rounded-full text-xs font-semibold border transition-all hover:scale-[1.03]"
        style={{
          paddingInline: 14,
          paddingBlock: 9,
          borderColor: SHELL_COLORS_INDIA_V2.cardBorder,
          color: SHELL_COLORS_INDIA_V2.bodyColor,
          background: SHELL_COLORS_INDIA_V2.white,
        }}
      >
        {copied ? (
          <Check className="w-3.5 h-3.5" style={{ color: accent }} />
        ) : (
          <Copy className="w-3.5 h-3.5" />
        )}
        {copied ? t.copied : t.copy}
      </button>
      <button
        disabled
        title="Coming soon"
        className="flex items-center gap-1.5 rounded-full text-xs font-semibold border transition-all opacity-50 cursor-not-allowed"
        style={{
          paddingInline: 14,
          paddingBlock: 9,
          borderColor: SHELL_COLORS_INDIA_V2.cardBorder,
          color: SHELL_COLORS_INDIA_V2.mutedColor,
          background: SHELL_COLORS_INDIA_V2.white,
        }}
      >
        <Download className="w-3.5 h-3.5" />
        {t.saveImage}
      </button>
    </div>
  );
};

export const IndiaV2LoadingState: React.FC<{ label?: string }> = ({
  label = "Reading your energy…",
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "70px 20px",
      gap: 16,
    }}
  >
    <div
      style={{
        width: 30,
        height: 30,
        border: `3px solid ${SHELL_COLORS_INDIA_V2.section}`,
        borderTopColor: SHELL_COLORS_INDIA_V2.accent,
        borderRadius: "50%",
      }}
      className="animate-spin"
    />
    <p style={{ fontSize: 13.5, color: SHELL_COLORS_INDIA_V2.mutedColor }}>
      {label}
    </p>
  </div>
);

export const IndiaV2ErrorState: React.FC<{
  message: string;
  onRetry: () => void;
  retryLabel?: string;
}> = ({ message, onRetry, retryLabel = "Try Again" }) => (
  <div
    style={{
      padding: "18px 20px",
      background: "#FBEDE9",
      border: "1px solid #E8C6BB",
      borderRadius: 14,
      color: "#95493A",
      fontSize: 14,
    }}
  >
    {message}
    <button
      onClick={onRetry}
      style={{
        display: "block",
        marginTop: 10,
        color: SHELL_COLORS_INDIA_V2.accent,
        fontWeight: 700,
        fontSize: 13,
      }}
    >
      {retryLabel}
    </button>
  </div>
);

export const IndiaV2ChatCta: React.FC<{ onClick: () => void; label?: string }> = ({
  onClick,
  label = "Continue in Chat",
}) => (
  <button
    onClick={onClick}
    className="flex items-center justify-center gap-2 rounded-full text-sm font-semibold text-white transition-all hover:scale-[1.02]"
    style={{
      marginTop: 24,
      width: "100%",
      background: `linear-gradient(135deg, ${SHELL_COLORS_INDIA_V2.accent} 0%, ${SHELL_COLORS_INDIA_V2.gold} 100%)`,
      boxShadow: `0 6px 20px ${SHELL_COLORS_INDIA_V2.accentSoft}`,
      paddingTop: 14,
      paddingBottom: 14,
    }}
  >
    <MessageCircleHeart className="w-4 h-4" />
    {label}
  </button>
);

// ─── Astria India V3 — signature-layer primitives ──────────────────────────
// Only ever rendered when the backend actually populates these fields (V3's
// astriaIndiaV3Service.js) — V2 responses never carry them, so these render
// nothing (return null) for every existing V2 reading. Purely additive: no
// existing export above is touched.

interface HighlightBadgeProps {
  text?: string | null;
}

// "{{rashi}} · {{nakshatra}}" bar per the client spec — small pill, soft
// grey background, minimal, sits above the rest of the card content.
export const AstriaIndiaV2HighlightBadge: React.FC<HighlightBadgeProps> = ({
  text,
}) => {
  if (!text || !text.trim()) return null;
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 12px",
        borderRadius: 999,
        background: "#F3F3F3",
        color: "#555555",
        fontSize: 12.5,
        fontWeight: 500,
        marginBottom: 16,
      }}
    >
      <span aria-hidden="true">✦</span>
      {text}
    </div>
  );
};

interface SignatureLineProps {
  value?: string | null;
  emphasis?: boolean;
}

// Shared renderer for the other 3 signature-layer fields (micro_imagery /
// astro_soft_influence / clarity_point) — each is one short soft line, so
// they share the same treatment, only `emphasis` (used for clarity_point)
// nudges the style slightly to stand out as the "so what" of the reading.
export const AstriaIndiaV2SignatureLine: React.FC<SignatureLineProps> = ({
  value,
  emphasis = false,
}) => {
  if (!value || !value.trim()) return null;
  return (
    <p
      style={{
        fontSize: emphasis ? 14.5 : 14,
        fontStyle: "italic",
        fontWeight: emphasis ? 600 : 400,
        color: emphasis ? SHELL_COLORS_INDIA_V2.accent : SHELL_COLORS_INDIA_V2.mutedColor,
        lineHeight: 1.7,
        margin: "0 0 14px 0",
        paddingLeft: emphasis ? 12 : 0,
        borderLeft: emphasis ? `2.5px solid ${SHELL_COLORS_INDIA_V2.gold}` : "none",
      }}
    >
      {value}
    </p>
  );
};

// Note: the V2/V3 engine toggle lives in AstriaIndiaV2Shell.tsx (not here)
// — Shell.tsx already owns SHELL_COLORS_INDIA_V2 locally and this file
// imports FROM Shell.tsx, so defining it there avoids a circular import.
